import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from "react-redux";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/loaders/Loader";
import PreTableServiceCredentials from "../../components/tables/PreTableServiceCredentials";
import CustomSelect from "../../components/CustomSelect";
import {USER_PROJECT, USER_ROLE} from "../../constants/Auth";
import CustomTooltip from "../../components/CustomTooltip";
import {logOut} from "../../redux/dataUploader/dataSlice";

const ServiceCredentials = () => {

  const dispatch = useDispatch();
  const token = localStorage.getItem("auth_token");
  const navigate = useNavigate();
  const role = localStorage.getItem(USER_ROLE);

  const [accountsList, setAccountsList] = useState([]);
  const [projectsList, setProjectsList] = useState([]);
  const [usersList, setUsersList] = useState({});
  const [manageCompaniesIdList, setManageCompaniesIdList] = useState({});
  const [columnDefs, setColumnDefs] = useState([]);
  const [isPopupType, setPopupType] = useState('updating');
  const [addPopupStatus, setAddPopupStatus] = useState(false);
  const [deletePopupStatus, setDeletePopupStatus] = useState(false);
  const [isTriggerUpdate, setIsTriggerUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [addPopupWithConfigData] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [reload, setReload] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const checkSession = (status) => {
      if(status === 401) {
          dispatch(logOut());
      }
  }

  useEffect(() => {
      if(reload) {
          getAllAccounts();
      }
  }, [reload])

  // Get Accounts
  const getAllAccounts = async (page = 1, per_page = perPage) => {
    try {
      setIsLoading(true);

      const res = await fetch(`${localStorage.getItem("apiUrl")}api/service-credentials?per_page=${per_page}&page=${page}`, {
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
      });

      checkSession(res.status);
      if (!res.ok) throw new Error("Failed to fetch accounts");
      
      const data = await res.json();
      setAccountsList(data?.accounts?.data || []);
      setTotalRows(data?.accounts?.total || 0);
      setCurrentPage(data?.accounts?.current_page);
      // await getAllProjects();
    } catch (error) {
      console.error("Server connection error:", error);
    } finally {
      setIsLoading(false);
    } 
  };

  useEffect(() => {
    if (reload) {
        getAllAccounts(currentPage, perPage);
    }
  }, [reload]);

  const toIdNameArray = (obj) => {
    if (!obj || typeof obj !== 'object') return [];

    const normalize = (s) =>
      String(s ?? '')
        .replace(/\s+/g, ' ')
        .trim();

    return Object.entries(obj)
      .map(([k, v]) => ({
        id: Number(k),
        name: normalize(v)
      }))
      .filter(item => Number.isFinite(item.id) && item.name.length > 0)
      .sort((a, b) => a.id - b.id);
  }
  
  // Get projects
  const getAllProjects = async () => {
    try {
      setIsLoading(true);

      const res = await fetch(`${localStorage.getItem("apiUrl")}api/managing-blogs-and-companies`, {
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
      });

      checkSession(res.status);
      if (!res.ok) throw new Error("Failed to fetch Accounts");

      const data = await res.json();
      
      setProjectsList(toIdNameArray(data?.projects) || []);
      setUsersList(data?.users || []);
      setManageCompaniesIdList(data?.companies || []);
    //   setTotalRows(data?.accounts?.total || 0);
    } catch (error) {
      console.error("Server connection error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAllAccounts();
  }, [])

  useEffect(() => {
    setAccountsList(prev =>
      prev.map(c => ({
        ...c,
        userName: usersList?.[c.user_id] ?? null
      }))
    );
    
  }, [usersList])

  useEffect(() => {
    setAccountsList(prev =>
      prev.map(c => ({
        ...c,
        manageCompany: manageCompaniesIdList?.[c.manage_companies_id] ?? null
      }))
    );
    
  }, [manageCompaniesIdList])

  useEffect(() => {
    async function update() {
        if (isTriggerUpdate) {
          getAllAccounts()
            return setIsTriggerUpdate(false)
        }
    }

    update()
  }, [isTriggerUpdate]);

  const modifiedColumnsData = (columns) => columns.map(el => {
      let columnDef = {
          field: el,
          filter: true,
          wrapText: true,
          tooltipField: el,
          tooltipComponent: CustomTooltip,
      };

      if (el === 'id') {
          columnDef = {
              ...columnDef,
              minWidth: 20,
              maxWidth: 80,
              hide: true
          }
      } else if (el === 'name') {
          columnDef = {
              ...columnDef,
              minWidth: 200,
              maxWidth: 400 
          }
      } else if (el === 'service_credential_type_id') {
          columnDef = {
              ...columnDef,
              minWidth: 100,
              maxWidth: 200 
          }
      } else if (el === 'credentials') {
          columnDef = {
              ...columnDef,
              minWidth: 300,
              maxWidth: 450 
          }
      } else if (el === 'status') {
          columnDef = {
              ...columnDef,
              minWidth: 100,
              maxWidth: 240 
          }
      }else if (el === 'created_at') {
          columnDef = {
              ...columnDef,
              minWidth: 140,
              maxWidth: 240 
          }
      }else if (el === 'updated_at') {
          columnDef = {
              ...columnDef,
              minWidth: 140,
              maxWidth: 240 
          }
      }else if (el === 'account') {
          columnDef = {
              ...columnDef,
              minWidth: 100,
              maxWidth: 240 
          }
      } else if (el === 'magic_company_short_name') {
          columnDef = {
              ...columnDef,
              minWidth: 200,
              maxWidth: 240 
          }
      }else if (el === 'is_magic_enabled') {
          columnDef = {
              ...columnDef,
              minWidth: 120,
              maxWidth: 200 
          }
      }else if (el === 'company_id') {
          columnDef = {
              ...columnDef,
              minWidth: 120,
              maxWidth: 240 
          }
      }else if (el === 'is_archived') {
          columnDef = {
              ...columnDef,
              minWidth: 120,
              maxWidth: 240 
          }
      } else if (el === 'offers_parent_id') {
          columnDef = {
              ...columnDef,
              minWidth: 120,
              maxWidth: 240 
          }
      } else if (el === 'is_main_offers') {
          columnDef = {
              ...columnDef,
              minWidth: 120,
              maxWidth: 240 
          }
      } else if (el === 'is_main_commissions') {
          columnDef = {
              ...columnDef,
              minWidth: 120,
              maxWidth: 240 
          }
      } else if (el === 'commissions_parent_id') {
          columnDef = {
              ...columnDef,
              minWidth: 120,
              maxWidth: 240 
          }
      } else if (el === 'voluum_id') {
          columnDef = {
              ...columnDef,
              minWidth: 100,
              maxWidth: 240 
          }
      } else if (el === 'blog_id') {
          columnDef = {
              ...columnDef,
              minWidth: 100,
              maxWidth: 240 
          }
      } else if (el === 'affise_affiliate_id') {
          columnDef = {
              ...columnDef,
              minWidth: 120,
              maxWidth: 240 
          }
      } else if (el === 'deleted_at') {
          columnDef = {
              ...columnDef,
              minWidth: 120,
              maxWidth: 240 
          }
      } else if (el === 'User Name' && role !== 'admin') {
          columnDef = {
              ...columnDef,
              minWidth: 300,
              maxWidth: 360,
              hide: true
          }
      } else if (el === 'manageCompany') {
          columnDef = {
              ...columnDef,
              minWidth: 300,
              maxWidth: 360 ,
              hide: true
          }
      } 
      return columnDef;
  })

  if (isLoading) {
      return <div className='loading-wrap'><Loader/></div>
  }

  return (
    <div>
      {     
        accountsList.length > 0 && 
        <PreTableServiceCredentials
            rowData={accountsList}
            columnDefs={columnDefs}
            setColumnDefs={setColumnDefs}
            addPopupStatus={addPopupStatus}
            setAddPopupStatus={setAddPopupStatus}
            initialValues={initialValues}
            setInitialValues={setInitialValues}
            modifiedColumnsData={modifiedColumnsData}
            addPopupWithConfigData={addPopupWithConfigData}
            isPopupType={isPopupType}
            setPopupType={setPopupType}
            // remarksList={remarksList}
            setDeletePopupStatus={setDeletePopupStatus}
            deletePopupStatus={deletePopupStatus}
            isDeleteButton={true}
            setIsTriggerUpdate={setIsTriggerUpdate}
            setAccountsList={setAccountsList}
            projectsList={projectsList}
            usersList={usersList}
            manageCompaniesIdList={manageCompaniesIdList}
            setReload={setReload}
            reload={reload}
            totalRows={totalRows}
            currentPage={currentPage}
            perPage={perPage}
            onPageChange={(page) => getAllAccounts(page, perPage)}
            onPerPageChange={(newPerPage) => {
                setPerPage(newPerPage);
                getAllAccounts(1, newPerPage);
            }}
        />
      }
    </div>
  )
}

export default ServiceCredentials;